<script setup>
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { inject } from "vue";
const viewportSize = inject('composables').useViewportSize();
const isMobile = viewportSize.isMobile;

function isEmptyValue(dataValue) {
  return (
      dataValue === []
      || dataValue === {}
      || dataValue === ''
      || dataValue === null
  )
}
</script>

<script>
import isEmpty from 'lodash/isEmpty';
import {mapState} from 'pinia';
import { useEnvStore } from '@dutypay/store-modules'
// import {expect} from "chai";
import DpReadValue from "./DpReadValue";

const tests = [
  {
    title: "Single value",
    description: "We have a single value and no descriptive phrase for the value.",
    props: {
      data: {firstRow: "Some value as a string"},
    },
    assert: function(component) {
      expect(component.text()).to.include( "Some value as a string" );
      expect(component.text()).to.include( "firstRow" );
    }
  },
  {
    title: "Key map",
    description: "Set a string that describes the value on the right hand side. The keys have to match.",
    props: {
      data: {firstRow: "Some value as a string"},
      keyMap: {firstRow: "First row"},
    },
    assert: function(component) {
      expect(component.text()).to.include( "Some value as a string" );
      expect(component.text()).to.include( "First row" );
      expect(component.text()).to.not.include( "firstRow" );
    }
  },
  {
    title: "Key map incomplete",
    description: "Print more than one value. The keyMap is intentionally incomplete.",
    props: {
      data: {
        firstRow: "Some value as a string",
        secondRow: "Another value as a string",
      },
      keyMap: {
        firstRow: "A value",
      },
    },
    assert: function(component) {
      expect(component.text()).to.include( "Some value as a string" );
      expect(component.text()).to.include( "Another value as a string" );
      expect(component.text()).to.include( "A value" );
      expect(component.text()).to.not.include( "firstRow" );
      expect(component.text()).to.include( "secondRow" );
    }
  },
  {
    title: "Empty values",
    description: "Rows with empty values are ignored.",
    props: {
      data: {
        firstRow: "",
        secondRow: "Another value as a string",
      },
      keyMap: {
        firstRow: "A value",
      },
    },
    assert: function(component) {
      expect(component.text()).to.not.include( "Some value as a string" );
      expect(component.text()).to.include( "Another value as a string" );
      expect(component.text()).to.not.include( "A value" );
      expect(component.text()).to.include( "secondRow" );
    }
  },
  {
    title: "Show empty values",
    description: "Print empty values with showEmptyValues.",
    props: {
      data: {
        firstRow: "",
        secondRow: "Another value as a string",
      },
      keyMap: {
        firstRow: "A value",
      },
      showEmptyValues: true
    },
    assert: function(component) {
      expect(component.text()).to.not.include( "Some value as a string" );
      expect(component.text()).to.include( "Another value as a string" );
      expect(component.text()).to.include( "A value" );
      expect(component.text()).to.not.include( "firstRow" );
      expect(component.text()).to.include( "secondRow" );
    }
  },
  {
    title: "Show only mapped keys",
    description: "Hide values that have no corresponding key in keyMap.",
    props: {
      data: {
        firstRow: "Some value as a string",
        secondRow: "Another value as a string",
      },
      keyMap: {
        firstRow: "A value",
      },
      showOnlyMappedKeys: true
    },
    assert: function(component) {
      expect(component.text()).to.include( "Some value as a string" );
      expect(component.text()).to.not.include( "Another value as a string" );
      expect(component.text()).to.include( "A value" );
      expect(component.text()).to.not.include( "firstRow" );
      expect(component.text()).to.not.include( "secondRow" );
    }
  },
  {
    title: "Enforce reading direction",
    description: "Overwrite reading direction for a set of keys.",
    props: {
      data: {
        firstRow: "+49 (0)631 56 001 600",
        secondRow: "+49 (0)631 56 001 600",
      },
      keyMap: {
        firstRow: "Reading direction is RTL",
        secondRow: "Enforced LTR reading direction"
      },
      bdoKeyMap: {
        secondRow: 'ltr'
      }
    },
    style: {direction: 'rtl'},
    assert: function(component) {
      expect(component.find('bdo[dir="ltr"]').text()).to.contain( "+49 (0)631 56 001 600" );
    }
  },
  {
    title: "Set column styles",
    description: "Overwrite or add CSS rules for either the key column or value column.",
    props: {
      data: {
        firstRow: "Some value as a string"
      },
      keyMap: {
        firstRow: "A value"
      },
      keyColumnStyle: {textDecoration: 'underline'},
      dataColumnStyle: {color: '#ff0000'}
    },
    assert: function(component) {
      expect(component.find('.dp-read-values__grid__key-column[style="text-decoration: underline;"]').text()).to.contain('A value');
      expect(component.find('.dp-read-values__grid__read-value[style="color: rgb(255, 0, 0);"]').text()).to.contain('Some value as a string');
    }
  },
  {
    title: "Mobile view",
    description: "Toggle the mobile version of this component.",
    props: {
      data: {
        firstRow: "Some value as a string",
        secondRow: "Another value as a string",
      },
      keyMap: {
        firstRow: "A value",
        secondRow: "Another value",
      }
    }
  }
];

export default {
  name: "DpReadValues",
  description: "Print values of objects.",
  props: {
    data: {type: [Object, Array], required: true, desc: 'Value column'},
    keyMap: {type: Object, desc: 'Key column'},
    bdoKeyMap: {type: Object, desc: 'Key-value pair with bdo dir value for the key.'},
    showOnlyMappedKeys: {type: Boolean, default: false, desc: 'Hide key-value pairs that have no key in keyMap.'},
    showEmptyValues: {type: Boolean, default: false, desc: 'Show values that are empty.'},
    keyColumnStyle: {type: Object, desc: 'Overwrite styles for the key column.'},
    dataColumnStyle: {type: Object, desc: 'Overwrite styles for the value column.'}
  },
  components: {DpReadValue},
  // tests,
  stubs: {
    ElTag: {
      template: '<span><slot></slot></span>'
    }
  },
  computed: {
    isEmpty: () => {
      return isEmpty;
    },
    ...mapState(useEnvStore, ['countries'])
  }
}
</script>

<template>
  <div>
    <template v-for="(dataKey, index) in Object.keys(data)">
      <template v-if="(showEmptyValues || !isEmptyValue(data[dataKey])) && (showOnlyMappedKeys ? (keyMap !== undefined && keyMap[dataKey] !== undefined) : true)">
        <div v-show="isMobile" :key="`dp-read-values-index-${index}`">
          <DpReadValue :data-key="(keyMap) ? keyMap[dataKey] : dataKey"
                       :data-value="data[dataKey]"
                       :data-key-style="keyColumnStyle"
                       :data-value-style="dataColumnStyle"
                       :bdo="(bdoKeyMap && !isEmpty(bdoKeyMap[dataKey])) ? bdoKeyMap[dataKey] : undefined"/>
        </div>
        <div v-show="!isMobile" class="dp-read-values__grid" :key="`dp-read-values-index-${index}`">
          <div class="dp-read-values__grid__key-column" :style="keyColumnStyle">{{ (keyMap && keyMap[dataKey]) ? keyMap[dataKey] : dataKey }}</div>
          <div class="dp-read-values__grid__read-value" :style="dataColumnStyle">
            <template v-if="typeof data[dataKey] === 'string' || typeof data[dataKey] === 'number'">
              <bdo v-if="bdoKeyMap && !isEmpty(bdoKeyMap[dataKey])" :dir="bdoKeyMap[dataKey]">{{
                  data[dataKey]
                }}</bdo>
              <template v-else>{{ data[dataKey] }}</template>
            </template>
            <template v-else-if="Array.isArray(data[dataKey])">
              <ul>
                <li v-for="(item, index) in data[dataKey]" :key="`list${index}`">{{ item }}</li>
              </ul>
            </template>
            <template v-else-if="typeof data[dataKey] === 'object'">
              <div v-if="data[dataKey].type !== undefined && data[dataKey].type === 'media/image'"
                   :style="{height: data[dataKey].meta.displayAt.height, width: data[dataKey].meta.displayAt.width}">
                <div class="dp-read-values__grid__read-value__image"
                     :style="{'background-image': `url(${data[dataKey].url})`}"></div>
              </div>
              <div v-else-if="data[dataKey].type !== undefined && data[dataKey].type === 'script/country'">
                <span>{{ (countries !== undefined && !isEmpty(countries)) ? countries[data[dataKey].country[1]][$i18n.locale] : data[dataKey].country[1] }}</span>
              </div>
              <ul v-else>
                <li v-for="(itemKey, index) in Object.keys(data[dataKey])" :key="`object${index}`">
                  <template v-if="data[dataKey].hasOwnProperty(itemKey)"><span
                      style="color: var(--use-theme__event__info__font-color);">{{ (keyMap && keyMap[`${dataKey}.${itemKey}`]) ? keyMap[`${dataKey}.${itemKey}`] : itemKey }}</span>: {{ data[dataKey][itemKey] }}
                  </template>
                  <template v-else><i style="color: var(--use-theme__event__info__font-color);">{{ t('undefined.label') }}</i></template>
                </li>
              </ul>
            </template>
            <template v-else-if="typeof data[dataKey] === 'boolean'">
              <span v-if="data[dataKey] === true" class="dp-read-values__grid__read-value__boolean--true">{{
                  t('active.label')
                }}</span>
              <span v-if="data[dataKey] === false" class="dp-read-values__grid__read-value__boolean--false">{{
                  t('inactive.label')
                }}</span>
            </template>
            <template v-else-if="isEmptyValue(data[dataKey])"><span
                style="color: var(--use-theme__event__info__font-color);">{{ t('noEntry.label') }}</span>
            </template>
            <template v-else><i
                style="color: var(--use-theme__event__info__font-color);">{{ t('undefined.label') }}</i></template>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<style lang="scss">
.dp-read-values__grid {
  display: grid;
  align-items: start;
  padding: 1rem 0;
  grid-column-gap: 1rem;
  grid-row-gap: 1rem;
  grid-template-columns: 1fr 1fr;

  &:not(:last-child) {
    border-bottom: 2px solid var(--use-theme__ui-gradient-1);
  }

  .key-column {
    color: #515151;
    font-size: 0.9em;
  }

  .dp-read-values__grid__read-value {
    color: var(--use-theme__font-color);

    .dp-read-values__grid__read-value__boolean {
      border-width: 2px;
      border-style: solid;
      padding: 0.2em 0.5em;
      border-radius: 4px;
    }
    .dp-read-values__grid__read-value__boolean--false {
      @extend .dp-read-values__grid__read-value__boolean;
      color: var(--use-theme__event__error__font-color);
      background-color: var(--use-theme__event__error__background-color);
      border-color: var(--use-theme__event__error__background-color);
    }
    .dp-read-values__grid__read-value__boolean--true {
      @extend .dp-read-values__grid__read-value__boolean;
      color: var(--use-theme__event__success__font-color);
      background-color: var(--use-theme__event__success__background-color);
      border-color: var(--use-theme__event__success__background-color);
    }

    .dp-read-values__grid__read-value__image {
      -webkit-border-radius: 6px;
      -moz-border-radius: 6px;
      border-radius: 6px;
      background-position: center;
      background-size: cover;
      width: 100%;
      height: 100%;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        margin-inline-start: 1.5rem;

        &:before {
          margin-inline-start: -1.5rem;
          margin-inline-end: 0.5rem;
          content: '—'
        }
      }
    }
  }
}
</style>

<i18n>
{
  "ar-AE": {
    "active": {
      "label": "تم التنشيط"
    },
    "inactive": {
      "label": "معطل"
    },
    "undefined": {
      "label": ""
    }
  },
  "ar-EG": {
    "active": {
      "label": "تم التنشيط"
    },
    "inactive": {
      "label": "معطل"
    },
    "undefined": {
      "label": ""
    }
  },
  "ar-SA": {
    "active": {
      "label": "تم التنشيط"
    },
    "inactive": {
      "label": "معطل"
    },
    "undefined": {
      "label": ""
    }
  },
  "el-GR": {
    "active": {
      "label": "Ενεργοποιήθηκε"
    },
    "inactive": {
      "label": "Απενεργοποιήθηκε"
    },
    "undefined": {
      "label": ""
    }
  },
  "el-CY": {
    "active": {
      "label": "Ενεργοποιήθηκε"
    },
    "inactive": {
      "label": "Απενεργοποιήθηκε"
    },
    "undefined": {
      "label": ""
    }
  },
  "de-DE": {
    "active": {
      "label": "Aktiviert"
    },
    "inactive": {
      "label": "Deaktiviert"
    },
    "undefined": {
      "label": ""
    }
  },
  "de-AT": {
    "active": {
      "label": "Aktiviert"
    },
    "inactive": {
      "label": "Deaktiviert"
    },
    "undefined": {
      "label": ""
    }
  },
  "de-CH": {
    "active": {
      "label": "Aktiviert"
    },
    "inactive": {
      "label": "Deaktiviert"
    },
    "undefined": {
      "label": ""
    }
  },
  "en-US": {
    "active": {
      "label": "Activated"
    },
    "inactive": {
      "label": "Deactivated"
    },
    "undefined": {
      "label": ""
    }
  },
  "en-GB": {
    "active": {
      "label": "Activated"
    },
    "inactive": {
      "label": "Deactivated"
    },
    "undefined": {
      "label": ""
    }
  },
  "it-IT": {
    "active": {
      "label": "Attivato"
    },
    "inactive": {
      "label": "Disattivato"
    },
    "undefined": {
      "label": ""
    }
  },
  "fr-FR": {
    "active": {
      "label": "Activé"
    },
    "inactive": {
      "label": "Désactivé"
    },
    "undefined": {
      "label": ""
    }
  },
  "es-ES": {
    "active": {
      "label": "Activada"
    },
    "inactive": {
      "label": "Desactivado"
    },
    "undefined": {
      "label": ""
    }
  },
  "pl-PL": {
    "active": {
      "label": "Aktywowano"
    },
    "inactive": {
      "label": "Dezaktywowane"
    },
    "undefined": {
      "label": ""
    }
  },
  "nl-NL": {
    "active": {
      "label": "Geactiveerd"
    },
    "inactive": {
      "label": "Gedeactiveerd"
    },
    "undefined": {
      "label": ""
    }
  },
  "bg-BG": {
    "active": {
      "label": "Активирано"
    },
    "inactive": {
      "label": "Деактивиран"
    },
    "undefined": {
      "label": ""
    }
  },
  "bs-BA": {
    "active": {
      "label": "Aktivirano"
    },
    "inactive": {
      "label": "Deaktivirano"
    },
    "undefined": {
      "label": ""
    }
  },
  "da-DK": {
    "active": {
      "label": "Aktiveret"
    },
    "inactive": {
      "label": "Deaktiveret"
    },
    "undefined": {
      "label": ""
    }
  },
  "et-EE": {
    "active": {
      "label": "Aktiveeritud"
    },
    "inactive": {
      "label": "Deaktiveeritud"
    },
    "undefined": {
      "label": ""
    }
  },
  "fi-FI": {
    "active": {
      "label": "Aktivoitu"
    },
    "inactive": {
      "label": "Ei käytössä"
    },
    "undefined": {
      "label": ""
    }
  },
  "he-IL": {
    "active": {
      "label": "מופעל"
    },
    "inactive": {
      "label": "מושבת"
    },
    "undefined": {
      "label": ""
    }
  },
  "ga-IE": {
    "active": {
      "label": "Gníomhachtaithe"
    },
    "inactive": {
      "label": "Díghníomhachtaithe"
    },
    "undefined": {
      "label": ""
    }
  },
  "ja-JP": {
    "active": {
      "label": "アクティブ化"
    },
    "inactive": {
      "label": "無効"
    },
    "undefined": {
      "label": ""
    }
  },
  "hr-HR": {
    "active": {
      "label": "Aktiviran"
    },
    "inactive": {
      "label": "Deaktivirano"
    },
    "undefined": {
      "label": ""
    }
  },
  "lv-LV": {
    "active": {
      "label": "Aktivizēts"
    },
    "inactive": {
      "label": "Deaktivizēts"
    },
    "undefined": {
      "label": ""
    }
  },
  "lt-LT": {
    "active": {
      "label": "Suaktyvinta"
    },
    "inactive": {
      "label": "Išjungta"
    },
    "undefined": {
      "label": ""
    }
  },
  "pt-PT": {
    "active": {
      "label": "Ativado"
    },
    "inactive": {
      "label": "Desativado"
    },
    "undefined": {
      "label": ""
    }
  },
  "ro-RO": {
    "active": {
      "label": "Activat"
    },
    "inactive": {
      "label": "Dezactivat"
    },
    "undefined": {
      "label": ""
    }
  },
  "ru-RU": {
    "active": {
      "label": "Активировано"
    },
    "inactive": {
      "label": "Деактивировано"
    },
    "undefined": {
      "label": ""
    }
  },
  "sv-SE": {
    "active": {
      "label": "Aktiverad"
    },
    "inactive": {
      "label": "Inaktiverad"
    },
    "undefined": {
      "label": ""
    }
  },
  "sk-SK": {
    "active": {
      "label": "Aktivované"
    },
    "inactive": {
      "label": "Deaktivované"
    },
    "undefined": {
      "label": ""
    }
  },
  "cs-CZ": {
    "active": {
      "label": "Aktivováno"
    },
    "inactive": {
      "label": "Deaktivováno"
    },
    "undefined": {
      "label": ""
    }
  },
  "hu-HU": {
    "active": {
      "label": "Aktivált"
    },
    "inactive": {
      "label": "Deaktiválva"
    },
    "undefined": {
      "label": ""
    }
  },
  "fr-BE": {
    "active": {
      "label": "Activé"
    },
    "inactive": {
      "label": "Désactivé"
    },
    "undefined": {
      "label": ""
    }
  },
  "fr-LU": {
    "active": {
      "label": "Activé"
    },
    "inactive": {
      "label": "Désactivé"
    },
    "undefined": {
      "label": ""
    }
  },
  "mt-MT": {
    "active": {
      "label": "Attivat"
    },
    "inactive": {
      "label": "Diżattivat"
    },
    "undefined": {
      "label": ""
    }
  },
  "sl-SI": {
    "active": {
      "label": "Aktivirano"
    },
    "inactive": {
      "label": "Deaktivirano"
    },
    "undefined": {
      "label": ""
    }
  },
  "tr-TR": {
    "active": {
      "label": "Etkinleştirildi"
    },
    "inactive": {
      "label": "Devre dışı bırakıldı"
    },
    "undefined": {
      "label": ""
    }
  },
  "tr-CY": {
    "active": {
      "label": "Etkinleştirildi"
    },
    "inactive": {
      "label": "Devre dışı bırakıldı"
    },
    "undefined": {
      "label": ""
    }
  },
  "uk-UA": {
    "active": {
      "label": "Активовано"
    },
    "inactive": {
      "label": "Вимкнено"
    },
    "undefined": {
      "label": ""
    }
  },
  "zh-CN": {
    "active": {
      "label": "已激活"
    },
    "inactive": {
      "label": "已停用"
    },
    "undefined": {
      "label": ""
    }
  }
}
</i18n>
